import { useEffect, useState, useRef } from 'react'
import styles from './TopMedia.module.scss'
import Button from 'components/common/Button/Button'
import animationStyles from 'components/common/Animation/Animation.module.scss'
import useElementVisible from 'components/common/Animation/useElementVisible'

interface Post {
  id: number
  title: {
    rendered: string
  }
  content: {
    rendered: string
  }
  date: string
  link: string
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string
    }>
    'wp:term'?: Array<Array<{ name: string }>> // カテゴリー用
  }
}

const TopMedia: React.FC = () => {
  const textRef = useRef<HTMLDivElement>(null)
  const isTextVisible = useElementVisible(textRef, 0.1)

  const leftRef = useRef<HTMLDivElement>(null)
  const isLeftVisible = useElementVisible(leftRef, 0.1)

  const rightRef = useRef<HTMLDivElement>(null)
  const isRightVisible = useElementVisible(rightRef, 0.1)

  const [posts, setPosts] = useState<Post[]>([])

  // WP REST APIからデータを取得
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch('https://aitria.co.jp/in-pocket/wp-json/wp/v2/posts?_embed')
        const data: Post[] = await response.json()
        setPosts(data)
      } catch (error) {
        console.error('Error fetching posts:', error)
      }
    }

    fetchPosts()
  }, [])

  const formatCreateDate = (dateString: string) => {
    const date = new Date(dateString)
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(
      2,
      '0',
    )}`
  }

  const postList = posts.slice(0, 3).map((post, i) => (
    <article key={post.id} className={`${styles.columnitem} Column_box${i + 1}`}>
      <a href={post.link}>
        <figure className={styles.imgContainer}>
          <img
            src={post._embedded?.['wp:featuredmedia']?.[0]?.source_url}
            className={styles.image}
            alt={post.title.rendered}
            width={280}
            height={186}
          />
        </figure>
        <div className={styles.textwrap}>
          <span className={styles.day}>{`${formatCreateDate(post.date)}`}</span>
          <h4 className={styles.column__title}>{post.title.rendered}</h4>
          <p className={styles.column__category}>
            {post._embedded?.['wp:term']?.[0]?.map((category) => category.name).join(', ') || '未分類'}
          </p>
        </div>
      </a>
    </article>
  ))

  return (
    <section className={styles.topMedia}>
      <div className={styles.inner}>
        <p
          className={`${styles.topMedia__text} ${animationStyles.fade} ${isTextVisible ? animationStyles.fadeOn : ''}`}
          ref={textRef}
        >
          多様な自社メディアを通じて、積極的な情報発信を実施。
          <br />
          社内外に知見を共有し、常に新しいナレッジを取り入れることで、学び続ける文化を育んでいます。
        </p>
        <div className={styles.media}>
          <h2
            className={`${styles.media__title} ${animationStyles.fade} ${isLeftVisible ? animationStyles.fadeOn : ''}`}
            ref={leftRef}
          >
            メディア
          </h2>
          <div
            className={`${styles.media__cts} ${animationStyles.fade} ${isRightVisible ? animationStyles.fadeOn3 : ''}`}
            ref={rightRef}
          >
            <div className={styles.inpocket}>
              <h3 className={styles.inpocket__title}>
                <span className={styles.inpocket__title__ja}>自社ブログ</span>
                <figure className={styles.inpocket__title__img}>
                  <img src="/img/common/logo_in-pocket.svg" alt="in-Pocket" width={180} height={33} />
                </figure>
              </h3>
              <div className={styles.inpocket__link}>
                <Button text="in-Pocket" href="/in-pocket/" />
              </div>
            </div>
            <div className={styles.scroll}>
              <div className={styles.post}>{postList}</div>
            </div>
            <ul className={styles.others}>
              <li className={styles.others__list}>
                <a href="https://tech.aitria.co.jp/" target="_blank" className={styles.others__list__link}>
                  <figure className={styles.others__list__link__img}>
                    <img
                      src="/img/common/icon_aitria.svg"
                      alt="Tech Blog"
                      width={24}
                      height={23}
                      className={styles.others__list__link__img__tech}
                    />
                  </figure>
                  <p className={styles.others__list__link__name}>
                    エンジニアチーム
                    <br />
                    Tech Blog
                  </p>
                </a>
              </li>
              <li className={styles.others__list}>
                <a href="https://note.com/aitria_design" target="_blank" className={styles.others__list__link}>
                  <figure className={styles.others__list__link__img}>
                    <img
                      src="/img/common/icon_note.svg"
                      alt="デザインチーム note"
                      width={17}
                      height={17}
                      className={styles.others__list__link__img__note}
                    />
                  </figure>
                  <p className={styles.others__list__link__name}>デザインチーム note</p>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}

export default TopMedia
